import { __decorate, __metadata } from "tslib";
import { elementDefine, eventShadow, eventWindow, mutationObserverLight, onConnectedAfter, onConnectedBodyShadow, queryShadow, resizeObserverLight } from "@dooboostore/simple-web-component";
const tagName = "cartesian-chart-3d";
function num(v, def) {
  if (v == null || v.trim() === "")
    return def;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function str(v) {
  if (v == null)
    return void 0;
  const t = v.trim();
  return t === "" ? void 0 : t;
}
function mat3Identity() {
  return [1, 0, 0, 0, 1, 0, 0, 0, 1];
}
function mat3Mul(A, B) {
  const r = mat3Identity();
  for (let c = 0; c < 3; c++) {
    for (let rr = 0; rr < 3; rr++) {
      let s = 0;
      for (let k = 0; k < 3; k++)
        s += A[k * 3 + rr] * B[c * 3 + k];
      r[c * 3 + rr] = s;
    }
  }
  return r;
}
function mat3RotY(a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [c, 0, -s, 0, 1, 0, s, 0, c];
}
function mat3RotX(a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [1, 0, 0, 0, c, s, 0, -s, c];
}
function mat3RotZ(a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [c, s, 0, -s, c, 0, 0, 0, 1];
}
function mat3Vec(m, x, y, z) {
  return [
    m[0] * x + m[3] * y + m[6] * z,
    m[1] * x + m[4] * y + m[7] * z,
    m[2] * x + m[5] * y + m[8] * z
  ];
}
function mat3FromEuler(yawDeg, pitchDeg, rollDeg) {
  const toR = Math.PI / 180;
  const Ry = mat3RotY(yawDeg * toR);
  const Rx = mat3RotX(pitchDeg * toR);
  const Rz = mat3RotZ(rollDeg * toR);
  return mat3Mul(Rz, mat3Mul(Rx, Ry));
}
function mat3Orthonormalize(m) {
  let x0 = m[0], x1 = m[1], x2 = m[2];
  let y0 = m[3], y1 = m[4], y2 = m[5];
  let z0 = m[6], z1 = m[7], z2 = m[8];
  let len = Math.hypot(x0, x1, x2) || 1;
  x0 /= len;
  x1 /= len;
  x2 /= len;
  const dot01 = y0 * x0 + y1 * x1 + y2 * x2;
  y0 -= dot01 * x0;
  y1 -= dot01 * x1;
  y2 -= dot01 * x2;
  len = Math.hypot(y0, y1, y2) || 1;
  y0 /= len;
  y1 /= len;
  y2 /= len;
  z0 = x1 * y2 - x2 * y1;
  z1 = x2 * y0 - x0 * y2;
  z2 = x0 * y1 - x1 * y0;
  return [x0, x1, x2, y0, y1, y2, z0, z1, z2];
}
var CartesianChart3D_default = (w) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j;
  const existing = w.customElements.get(tagName);
  if (existing)
    return existing;
  let CartesianChart3DImpl = class CartesianChart3DImpl extends w.HTMLElement {
    constructor() {
      super(...arguments);
      Object.defineProperty(this, "vectors", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "points", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "polygons", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "viewMat", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: mat3Identity()
      });
      Object.defineProperty(this, "zoom", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 1
      });
      Object.defineProperty(this, "dragging", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "dragLastX", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "dragLastY", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchLastDist", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchLastAngle", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "resizeRaf", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "canvas", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: void 0
      });
      Object.defineProperty(this, "railVal", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: { x: 0, y: 0, z: 0, r: 0 }
      });
      Object.defineProperty(this, "railDrag", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
    }
    reset() {
      const yaw = num(this.getAttribute("yaw"), 35);
      const pitch = num(this.getAttribute("pitch"), 25);
      const roll = num(this.getAttribute("roll"), 0);
      this.viewMat = mat3FromEuler(yaw, pitch, roll);
      this.zoom = 1;
      if (this.canvas)
        this.draw();
    }
    collect() {
      const vectors = [];
      this.querySelectorAll(":scope > vector3d").forEach((el) => {
        const v = ["x1", "y1", "z1", "x2", "y2", "z2"].map((k) => Number(el.getAttribute(k)));
        if (!v.every(Number.isFinite))
          return;
        vectors.push({
          x1: v[0],
          y1: v[1],
          z1: v[2],
          x2: v[3],
          y2: v[4],
          z2: v[5],
          color: str(el.getAttribute("color")) || "#6366f1",
          width: num(el.getAttribute("width"), 2),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("label-color"))
        });
      });
      this.vectors = vectors;
      const points = [];
      this.querySelectorAll(":scope > point3d").forEach((el) => {
        const x = Number(el.getAttribute("x")), y = Number(el.getAttribute("y")), z = Number(el.getAttribute("z"));
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z))
          return;
        points.push({
          x,
          y,
          z,
          color: str(el.getAttribute("color")) || "#10b981",
          size: num(el.getAttribute("size"), 4),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("label-color"))
        });
      });
      this.points = points;
      const polygons = [];
      this.querySelectorAll(":scope > polygon3d").forEach((el) => {
        const raw = (el.getAttribute("points") || "").trim();
        if (!raw)
          return;
        const points2 = [];
        raw.split(/[\s;|]+/).forEach((triple) => {
          const [sx, sy, sz] = triple.split(",");
          const x = Number(sx), y = Number(sy), z = Number(sz);
          if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z))
            points2.push({ x, y, z });
        });
        if (points2.length < 3)
          return;
        polygons.push({
          points: points2,
          color: str(el.getAttribute("color")) || "#6366f1",
          width: num(el.getAttribute("width"), 1.5),
          fill: str(el.getAttribute("fill") || el.getAttribute("fill-style")),
          label: str(el.getAttribute("label")),
          labelColor: str(el.getAttribute("label-color"))
        });
      });
      this.polygons = polygons;
    }
    onConnected() {
      console.info("[cc3d] orbit=matrix build=20260908d");
      const yaw = num(this.getAttribute("yaw"), 35);
      const pitch = num(this.getAttribute("pitch"), 25);
      const roll = num(this.getAttribute("roll"), 0);
      this.viewMat = mat3FromEuler(yaw, pitch, roll);
      this.collect();
      if (this.canvas)
        this.draw();
      this.railThumbAll();
    }
    onMutated() {
      this.collect();
      if (this.canvas)
        this.draw();
    }
    onResize() {
      if (!this.canvas)
        return;
      if (this.resizeRaf)
        cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        if (this.canvas)
          this.draw();
        this.railThumbAll();
      });
    }
    render() {
      return `
        <style>
          :host { display:block; position:relative; height:var(--cc-canvas-height, 320px); }
          #cc3d-wrap { display:flex; flex-direction:column; width:100%; height:100%; }
          #cc3d-mid { display:flex; flex:1; min-height:0; }
          #cc3d-stage { position:relative; flex:1; min-width:0; }
          #cc3d-canvas {
            display:block; width:100%; height:100%;
            touch-action:none; cursor:grab;
            background:#fff;
          }
          :host([disabled-drag]) #cc3d-canvas { cursor:default; }
          .rail { position:relative; touch-action:none; user-select:none; -webkit-user-select:none; cursor:pointer; display:none; }
          .rail .track { position:absolute; inset:0; background:rgba(15,23,42,0.18); border-radius:7px; }
          .thumb {
            position:absolute; background:#fff; border:1px solid #cbd5e1; border-radius:6px;
            box-shadow:0 1px 4px rgba(0,0,0,0.25);
          }
          .thumb.snap { transition:left .18s ease, top .18s ease; }
          #rail-x { height:14px; margin:4px 2px 0; }
          #rail-x .thumb { top:2px; bottom:2px; width:22px; }
          #rail-r { height:14px; margin:0 2px 4px; }
          #rail-r .thumb { top:2px; bottom:2px; width:22px; }
          .rail.v { width:14px; margin:0 2px; align-self:stretch; flex-shrink:0; }
          .rail.v .thumb { left:2px; right:2px; height:22px; }
          :host([hide-controls]) .rail { display:none; }
          :host([show-rail-controller]) #rail-r,
          :host([show-rail-controller]) #rail-x { display:block; }
          :host([show-rail-controller]) #rail-y,
          :host([show-rail-controller]) #rail-z { display:flex; }
          #cc3d-reset {
            position:absolute; top:6px; right:6px; z-index:2;
            width:26px; height:26px; border-radius:6px;
            border:1px solid #e2e8f0; background:rgba(255,255,255,0.92);
            color:#64748b; font-size:13px; cursor:pointer; line-height:1; padding:0;
          }
          #cc3d-reset:hover { background:#f1f5f9; }
        </style>
        <div id="cc3d-wrap">
          <div class="rail" id="rail-r" data-kind="r" title="\uD654\uBA74 \uD68C\uC804(roll)"><div class="track"></div><div class="thumb"></div></div>
          <div id="cc3d-mid">
            <div class="rail v" id="rail-z" data-kind="z" title="\uC90C \uC778/\uC544\uC6C3"><div class="track"></div><div class="thumb"></div></div>
            <div id="cc3d-stage">
              <canvas id="cc3d-canvas"></canvas>
              <button id="cc3d-reset" title="\uC790\uC138 \uCD08\uAE30\uD654">\u27F2</button>
            </div>
            <div class="rail v" id="rail-y" data-kind="y" title="\uC0C1\uD558 \uD68C\uC804"><div class="track"></div><div class="thumb"></div></div>
          </div>
          <div class="rail" id="rail-x" data-kind="x" title="\uC88C\uC6B0 \uD68C\uC804"><div class="track"></div><div class="thumb"></div></div>
        </div>
      `;
    }
    getExtent() {
      const r = this.getAttribute("range");
      if (r != null && r.trim() !== "" && Number.isFinite(Number(r)) && Number(r) > 0) {
        return Number(r);
      }
      let m = 1;
      this.vectors.forEach((v) => {
        m = Math.max(m, Math.abs(v.x1), Math.abs(v.y1), Math.abs(v.z1), Math.abs(v.x2), Math.abs(v.y2), Math.abs(v.z2));
      });
      this.points.forEach((p) => {
        m = Math.max(m, Math.abs(p.x), Math.abs(p.y), Math.abs(p.z));
      });
      this.polygons.forEach((s) => s.points.forEach((p) => {
        m = Math.max(m, Math.abs(p.x), Math.abs(p.y), Math.abs(p.z));
      }));
      return m * 1.25;
    }
    project(x, y, z, view) {
      const [cx, cy, cz] = mat3Vec(this.viewMat, x, y, z);
      return {
        sx: view.cx + cx * view.scale,
        sy: view.cy - cy * view.scale,
        depth: cz
      };
    }
    haloText(ctx, text, x, y) {
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 3;
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
      ctx.restore();
    }
    draw() {
      const canvas = this.canvas;
      if (!canvas)
        return;
      const dpr = w.devicePixelRatio || 1;
      const cssW = canvas.clientWidth || 400;
      const cssH = canvas.clientHeight || 300;
      if (cssW < 40 || cssH < 40)
        return;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx)
        return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const pad = 14;
      const plotW = Math.max(10, cssW - pad * 2);
      const plotH = Math.max(10, cssH - pad * 2);
      const extent = this.getExtent();
      const scale = Math.min(plotW, plotH) / 2 / extent * this.zoom;
      const view = { cx: pad + plotW / 2, cy: pad + plotH / 2, scale };
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, cssW, cssH);
      ctx.save();
      ctx.beginPath();
      ctx.rect(pad, pad, plotW, plotH);
      ctx.clip();
      if (!this.hasAttribute("hide-grid")) {
        const E0 = extent;
        const faces = [
          [[-E0, -E0, -E0], [E0, -E0, -E0], [E0, E0, -E0], [-E0, E0, -E0]],
          [[-E0, -E0, E0], [E0, -E0, E0], [E0, E0, E0], [-E0, E0, E0]],
          [[-E0, -E0, -E0], [E0, -E0, -E0], [E0, -E0, E0], [-E0, -E0, E0]],
          [[-E0, E0, -E0], [E0, E0, -E0], [E0, E0, E0], [-E0, E0, E0]],
          [[-E0, -E0, -E0], [-E0, E0, -E0], [-E0, E0, E0], [-E0, -E0, E0]],
          [[E0, -E0, -E0], [E0, E0, -E0], [E0, E0, E0], [E0, -E0, E0]]
        ];
        ctx.fillStyle = "#f4f5f7";
        for (const f of faces) {
          ctx.beginPath();
          f.forEach((p, k) => {
            const s = this.project(p[0], p[1], p[2], view);
            if (k === 0)
              ctx.moveTo(s.sx, s.sy);
            else
              ctx.lineTo(s.sx, s.sy);
          });
          ctx.closePath();
          ctx.fill();
        }
        const raw = 2 * extent / 6;
        const gmag = Math.pow(10, Math.floor(Math.log10(raw)));
        const gnorm = raw / gmag;
        const step = (gnorm >= 5 ? 5 : gnorm >= 2 ? 2 : 1) * gmag;
        const ticks = [];
        for (let k = Math.ceil(-extent / step); k <= Math.floor(extent / step); k++) {
          ticks.push(Math.round(k * step * 1e9) / 1e9);
        }
        const E = extent;
        const seg = (a, b, color, width) => {
          const s1 = this.project(a[0], a[1], a[2], view);
          const s2 = this.project(b[0], b[1], b[2], view);
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.beginPath();
          ctx.moveTo(s1.sx, s1.sy);
          ctx.lineTo(s2.sx, s2.sy);
          ctx.stroke();
        };
        for (const v of ticks) {
          if (Math.abs(Math.abs(v) - E) < 1e-9)
            continue;
          seg([v, -E, -E], [v, E, -E], "#e5e7eb", 1);
          seg([v, -E, E], [v, E, E], "#e5e7eb", 1);
          seg([-E, v, -E], [E, v, -E], "#e5e7eb", 1);
          seg([-E, v, E], [E, v, E], "#e5e7eb", 1);
          seg([-E, -E, v], [E, -E, v], "#e5e7eb", 1);
          seg([-E, E, v], [E, E, v], "#e5e7eb", 1);
          seg([v, -E, -E], [v, -E, E], "#e5e7eb", 1);
          seg([v, E, -E], [v, E, E], "#e5e7eb", 1);
          seg([-E, v, -E], [-E, v, E], "#e5e7eb", 1);
          seg([E, v, -E], [E, v, E], "#e5e7eb", 1);
          seg([-E, -E, v], [-E, E, v], "#e5e7eb", 1);
          seg([E, -E, v], [E, E, v], "#e5e7eb", 1);
        }
        const C = [
          [-E, -E, -E],
          [E, -E, -E],
          [E, E, -E],
          [-E, E, -E],
          [-E, -E, E],
          [E, -E, E],
          [E, E, E],
          [-E, E, E]
        ];
        const EDGES = [
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 0],
          [4, 5],
          [5, 6],
          [6, 7],
          [7, 4],
          [0, 4],
          [1, 5],
          [2, 6],
          [3, 7]
        ];
        for (const [i, j] of EDGES)
          seg(C[i], C[j], "#4b5563", 1);
        ctx.font = "9px -apple-system,sans-serif";
        ctx.fillStyle = "#6b7280";
        const fmtT = (n) => String(parseFloat(n.toFixed(2)));
        const tickMark = (p, text, align, dx, dy) => {
          const s = this.project(p[0], p[1], p[2], view);
          ctx.fillStyle = "#6b7280";
          ctx.beginPath();
          ctx.arc(s.sx, s.sy, 1.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.textAlign = align;
          ctx.fillText(text, s.sx + dx, s.sy + dy);
        };
        const tickEdges = [
          { axis: 0, fixed: [-E, -E], align: "center", dx: 0, dy: 12 },
          { axis: 0, fixed: [E, E], align: "center", dx: 0, dy: -8 },
          { axis: 1, fixed: [E, -E], align: "center", dx: 0, dy: 12 },
          { axis: 1, fixed: [-E, E], align: "center", dx: 0, dy: -8 },
          { axis: 2, fixed: [E, E], align: "left", dx: 6, dy: 3 },
          { axis: 2, fixed: [-E, -E], align: "right", dx: -6, dy: 3 }
        ];
        for (const e of tickEdges) {
          for (const v of ticks) {
            const p = e.axis === 0 ? [v, e.fixed[0], e.fixed[1]] : e.axis === 1 ? [e.fixed[0], v, e.fixed[1]] : [e.fixed[0], e.fixed[1], v];
            tickMark(p, fmtT(v), e.align, e.dx, e.dy);
          }
        }
      }
      const axes = [
        { p1: [-extent, 0, 0], p2: [extent, 0, 0], color: "#e5484d", label: "X" },
        { p1: [0, -extent, 0], p2: [0, extent, 0], color: "#10b981", label: "Y" },
        { p1: [0, 0, -extent], p2: [0, 0, extent], color: "#3e63dd", label: "Z" }
      ];
      ctx.lineWidth = 1;
      for (const a of axes) {
        const s1 = this.project(a.p1[0], a.p1[1], a.p1[2], view);
        const s2 = this.project(a.p2[0], a.p2[1], a.p2[2], view);
        ctx.strokeStyle = a.color;
        ctx.beginPath();
        ctx.moveTo(s1.sx, s1.sy);
        ctx.lineTo(s2.sx, s2.sy);
        ctx.stroke();
        ctx.fillStyle = a.color;
        ctx.font = "bold 11px -apple-system,sans-serif";
        ctx.textAlign = "left";
        this.haloText(ctx, a.label, s2.sx + 4, s2.sy - 4);
      }
      for (const poly of this.polygons) {
        ctx.save();
        ctx.beginPath();
        poly.points.forEach((pt, k) => {
          const s = this.project(pt.x, pt.y, pt.z, view);
          if (k === 0)
            ctx.moveTo(s.sx, s.sy);
          else
            ctx.lineTo(s.sx, s.sy);
        });
        ctx.closePath();
        if (poly.fill) {
          ctx.fillStyle = poly.fill;
          ctx.fill();
        }
        ctx.strokeStyle = poly.color || "#6366f1";
        ctx.lineWidth = poly.width ?? 1.5;
        ctx.stroke();
        ctx.restore();
        if (poly.label) {
          const cx = poly.points.reduce((s2, p) => s2 + p.x, 0) / poly.points.length;
          const cy = poly.points.reduce((s2, p) => s2 + p.y, 0) / poly.points.length;
          const cz = poly.points.reduce((s2, p) => s2 + p.z, 0) / poly.points.length;
          const s = this.project(cx, cy, cz, view);
          ctx.fillStyle = poly.labelColor || poly.color || "#6366f1";
          ctx.font = "bold 11px -apple-system,sans-serif";
          ctx.textAlign = "center";
          this.haloText(ctx, poly.label, s.sx, s.sy);
        }
      }
      const items = [
        ...this.vectors.map((v) => ({
          kind: "v",
          depth: (this.project(v.x1, v.y1, v.z1, view).depth + this.project(v.x2, v.y2, v.z2, view).depth) / 2,
          v
        })),
        ...this.points.map((p) => ({
          kind: "p",
          depth: this.project(p.x, p.y, p.z, view).depth,
          p
        }))
      ];
      items.sort((m, n) => m.depth - n.depth);
      for (const it of items) {
        if (it.kind === "v") {
          const v = it.v;
          const s1 = this.project(v.x1, v.y1, v.z1, view);
          const s2 = this.project(v.x2, v.y2, v.z2, view);
          if (Math.hypot(s2.sx - s1.sx, s2.sy - s1.sy) < 2)
            continue;
          ctx.save();
          ctx.strokeStyle = v.color || "#6366f1";
          ctx.fillStyle = v.color || "#6366f1";
          ctx.lineWidth = v.width ?? 2;
          ctx.beginPath();
          ctx.moveTo(s1.sx, s1.sy);
          ctx.lineTo(s2.sx, s2.sy);
          ctx.stroke();
          const ang = Math.atan2(s2.sy - s1.sy, s2.sx - s1.sx);
          const h = 8;
          ctx.beginPath();
          ctx.moveTo(s2.sx, s2.sy);
          ctx.lineTo(s2.sx - h * Math.cos(ang - 0.4), s2.sy - h * Math.sin(ang - 0.4));
          ctx.lineTo(s2.sx - h * Math.cos(ang + 0.4), s2.sy - h * Math.sin(ang + 0.4));
          ctx.closePath();
          ctx.fill();
          ctx.restore();
          if (v.label) {
            ctx.fillStyle = v.labelColor || v.color || "#6366f1";
            ctx.font = "bold 11px -apple-system,sans-serif";
            ctx.textAlign = "left";
            this.haloText(ctx, v.label, s2.sx + 6, s2.sy - 6);
          }
        } else {
          const p = it.p;
          const s = this.project(p.x, p.y, p.z, view);
          ctx.save();
          ctx.fillStyle = p.color || "#10b981";
          ctx.beginPath();
          ctx.arc(s.sx, s.sy, p.size ?? 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          if (p.label) {
            ctx.fillStyle = p.labelColor || p.color || "#10b981";
            ctx.font = "bold 11px -apple-system,sans-serif";
            ctx.textAlign = "left";
            this.haloText(ctx, p.label, s.sx + 7, s.sy - 6);
          }
        }
      }
      ctx.restore();
    }
    /** 화면 기준 드래그(dx=화면 오른쪽, dy=화면 아래)를 카메라 로컬 축으로 회전 (카메라식).
     *  - 카메라는 드래그를 따라가고 내용은 반대로: 오른쪽 드래그 → 카메라는 오른쪽으로, 내용은 왼쪽으로
     *  - 항상 현재 보이는 화면 기준이므로 앞면/뒷면/뒤집힘/roll 무관하게 직관적. */
    orbitBy(dx, dy) {
      const sens = 0.4 * (Math.PI / 180);
      const Ry = mat3RotY(dx * sens);
      const Rx = mat3RotX(dy * sens);
      this.viewMat = mat3Orthonormalize(mat3Mul(mat3Mul(Ry, Rx), this.viewMat));
    }
    /** 두 손가락 트위스트: 화면 Z축(깊이) 주위 roll */
    rollBy(dAngleDeg) {
      const Rz = mat3RotZ(dAngleDeg * (Math.PI / 180));
      this.viewMat = mat3Orthonormalize(mat3Mul(Rz, this.viewMat));
    }
    railEl(kind) {
      return this.shadowRoot?.querySelector(`#rail-${kind}`);
    }
    railThumb(kind) {
      const rail = this.railEl(kind);
      const thumb = rail?.querySelector(".thumb");
      if (!rail || !thumb)
        return;
      const f = Math.max(-1, Math.min(1, this.railVal[kind] / 100));
      if (kind === "x" || kind === "r") {
        const W = rail.clientWidth, half = Math.max(0, (W - 22) / 2);
        thumb.style.left = `${(W - 22) / 2 + f * half}px`;
      } else {
        const H = rail.clientHeight, half = Math.max(0, (H - 22) / 2);
        thumb.style.top = `${(H - 22) / 2 + f * half}px`;
      }
    }
    railThumbAll() {
      this.railThumb("x");
      this.railThumb("y");
      this.railThumb("z");
      this.railThumb("r");
    }
    railValueFromPos(kind, clientX, clientY) {
      const rail = this.railEl(kind);
      if (!rail)
        return 0;
      const r = rail.getBoundingClientRect();
      const ratio = kind === "x" || kind === "r" ? (clientX - r.left) / Math.max(1, r.width) : (clientY - r.top) / Math.max(1, r.height);
      return Math.max(-100, Math.min(100, ratio * 200 - 100));
    }
    railOf(e) {
      const rail = e.target?.closest?.(".rail");
      if (!rail)
        return null;
      const kind = rail.dataset.kind;
      if (kind !== "x" && kind !== "y" && kind !== "z" && kind !== "r")
        return null;
      return { rail, kind };
    }
    onRailDown(e) {
      const found = this.railOf(e);
      if (!found)
        return;
      const { rail, kind } = found;
      try {
        rail.setPointerCapture(e.pointerId);
      } catch {
      }
      const thumb = rail.querySelector(".thumb");
      thumb?.classList.remove("snap");
      const v = this.railValueFromPos(kind, e.clientX, e.clientY);
      this.railVal[kind] = v;
      this.railDrag = { kind, last: v };
      this.railThumb(kind);
    }
    onRailMove(e) {
      if (!this.railDrag)
        return;
      const { kind, last } = this.railDrag;
      const v = this.railValueFromPos(kind, e.clientX, e.clientY);
      const d = v - last;
      if (d === 0)
        return;
      this.railVal[kind] = v;
      this.railDrag.last = v;
      if (kind === "x")
        this.orbitBy(d, 0);
      else if (kind === "y")
        this.orbitBy(0, d);
      else if (kind === "r")
        this.rollBy(d * 1.2);
      else
        this.zoom = Math.max(0.3, Math.min(5, this.zoom * (1 - d * 4e-3)));
      this.railThumb(kind);
      this.draw();
    }
    railRelease() {
      if (!this.railDrag)
        return;
      const { kind } = this.railDrag;
      this.railDrag = null;
      this.railVal[kind] = 0;
      const rail = this.railEl(kind);
      const thumb = rail?.querySelector(".thumb");
      thumb?.classList.add("snap");
      this.railThumb(kind);
    }
    onRailUp() {
      this.railRelease();
    }
    onRailCancel() {
      this.railRelease();
    }
    onMouseDown(e) {
      if (this.hasAttribute("disabled-drag"))
        return;
      this.dragging = true;
      this.dragLastX = e.clientX;
      this.dragLastY = e.clientY;
    }
    onMouseMove(e) {
      if (!this.dragging || this.hasAttribute("disabled-drag"))
        return;
      this.orbitBy(e.clientX - this.dragLastX, e.clientY - this.dragLastY);
      this.dragLastX = e.clientX;
      this.dragLastY = e.clientY;
      this.draw();
    }
    onMouseUp() {
      this.dragging = false;
    }
    onMouseLeave() {
      this.dragging = false;
    }
    onWheel(e) {
      if (this.hasAttribute("disabled-drag"))
        return;
      e.preventDefault();
      const delta = Math.max(-120, Math.min(120, e.deltaY));
      const factor = Math.exp(-delta * 55e-5 * 8);
      this.zoom = Math.max(0.3, Math.min(5, this.zoom * Math.max(0.94, Math.min(1.06, factor))));
      this.draw();
    }
    touchDist(a, b) {
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }
    touchAngle(a, b) {
      return Math.atan2(a.clientY - b.clientY, a.clientX - b.clientX) * 180 / Math.PI;
    }
    onTouchStart(e) {
      if (this.hasAttribute("disabled-drag"))
        return;
      e.preventDefault();
      if (e.touches.length === 2) {
        this.dragging = false;
        this.pinchLastDist = this.touchDist(e.touches[0], e.touches[1]);
        this.pinchLastAngle = this.touchAngle(e.touches[0], e.touches[1]);
        return;
      }
      if (e.touches.length !== 1) {
        this.dragging = false;
        return;
      }
      this.dragging = true;
      this.dragLastX = e.touches[0].clientX;
      this.dragLastY = e.touches[0].clientY;
    }
    onTouchMove(e) {
      if (this.hasAttribute("disabled-drag"))
        return;
      e.preventDefault();
      if (e.touches.length === 2) {
        const dist = this.touchDist(e.touches[0], e.touches[1]);
        const angle = this.touchAngle(e.touches[0], e.touches[1]);
        if (this.pinchLastDist > 0 && dist > 0) {
          this.zoom = Math.max(0.3, Math.min(5, this.zoom * (dist / this.pinchLastDist)));
        }
        let dAngle = angle - this.pinchLastAngle;
        if (dAngle > 180)
          dAngle -= 360;
        else if (dAngle < -180)
          dAngle += 360;
        this.rollBy(-dAngle);
        this.pinchLastDist = dist;
        this.pinchLastAngle = angle;
        this.dragging = false;
        this.draw();
        return;
      }
      if (!this.dragging || e.touches.length !== 1)
        return;
      this.orbitBy(e.touches[0].clientX - this.dragLastX, e.touches[0].clientY - this.dragLastY);
      this.dragLastX = e.touches[0].clientX;
      this.dragLastY = e.touches[0].clientY;
      this.draw();
    }
    onTouchEnd(e) {
      this.pinchLastDist = 0;
      if (e.touches.length === 1 && !this.hasAttribute("disabled-drag")) {
        this.dragging = true;
        this.dragLastX = e.touches[0].clientX;
        this.dragLastY = e.touches[0].clientY;
      } else {
        this.dragging = false;
      }
    }
    onTouchCancel() {
      this.dragging = false;
      this.pinchLastDist = 0;
    }
    onResetClick() {
      this.reset();
    }
  };
  __decorate([
    onConnectedAfter,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onConnected", null);
  __decorate([
    mutationObserverLight({ childList: true, attributes: true, subtree: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onMutated", null);
  __decorate([
    resizeObserverLight,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onResize", null);
  __decorate([
    onConnectedBodyShadow,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", String)
  ], CartesianChart3DImpl.prototype, "render", null);
  __decorate([
    queryShadow("#cc3d-canvas"),
    __metadata("design:type", typeof (_a = typeof HTMLCanvasElement !== "undefined" && HTMLCanvasElement) === "function" ? _a : Object)
  ], CartesianChart3DImpl.prototype, "canvas", void 0);
  __decorate([
    eventShadow(".rail", "pointerdown"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_b = typeof PointerEvent !== "undefined" && PointerEvent) === "function" ? _b : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onRailDown", null);
  __decorate([
    eventShadow(".rail", "pointermove"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_c = typeof PointerEvent !== "undefined" && PointerEvent) === "function" ? _c : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onRailMove", null);
  __decorate([
    eventShadow(".rail", "pointerup"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onRailUp", null);
  __decorate([
    eventShadow(".rail", "pointercancel"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onRailCancel", null);
  __decorate([
    eventShadow("#cc3d-canvas", "mousedown"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_d = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _d : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onMouseDown", null);
  __decorate([
    eventShadow("#cc3d-canvas", "mousemove"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_e = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _e : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onMouseMove", null);
  __decorate([
    eventWindow("mouseup"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onMouseUp", null);
  __decorate([
    eventShadow("#cc3d-canvas", "mouseleave"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onMouseLeave", null);
  __decorate([
    eventShadow("#cc3d-canvas", "wheel", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_f = typeof WheelEvent !== "undefined" && WheelEvent) === "function" ? _f : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onWheel", null);
  __decorate([
    eventShadow("#cc3d-canvas", "touchstart", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_g = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _g : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onTouchStart", null);
  __decorate([
    eventShadow("#cc3d-canvas", "touchmove", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_h = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _h : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onTouchMove", null);
  __decorate([
    eventShadow("#cc3d-canvas", "touchend"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_j = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _j : Object]),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onTouchEnd", null);
  __decorate([
    eventShadow("#cc3d-canvas", "touchcancel"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onTouchCancel", null);
  __decorate([
    eventShadow("#cc3d-reset", "click"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], CartesianChart3DImpl.prototype, "onResetClick", null);
  CartesianChart3DImpl = __decorate([
    elementDefine(tagName, { window: w })
  ], CartesianChart3DImpl);
  return CartesianChart3DImpl;
};
export {
  CartesianChart3D_default as default
};
//# sourceMappingURL=CartesianChart3D.js.map
